import { useSharedAPI } from "@certn/api";
import {
  InnerContentItem,
  OuterContentItem,
  PrimaryTag,
} from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { ArrayControlProps } from "@jsonforms/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Credential {
  credential_description: string;
  license_id_number?: string;
  issuing_institution: string;
  issuing_country?: string;
  issuing_region?: string;
  issue_date?: string;
  expiration_date?: string;
  in_progress?: boolean;
}

/**
 * Formats a country code to its display name using Intl.DisplayNames
 */
function formatCountryName(
  countryCode: string | undefined,
  locale: string
): string | undefined {
  if (!countryCode) return undefined;
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: "region" });
    return displayNames.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
}

/**
 * Individual credential summary item that fetches its own subdivision data
 * This allows credentials from different countries to each display correct region names
 */
function CredentialSummaryItem({
  credential,
  index,
}: {
  credential: Credential;
  index: number;
}) {
  const { t, i18n } = useTranslation();
  const now = new Date();

  // Fetch subdivisions for this credential's country (only if country is specified)
  const { data: subdivisions } = useSharedAPI(
    credential.issuing_country
      ? "/api/internal/shared/country-subdivisions/"
      : null,
    {
      alpha_2: credential.issuing_country,
    }
  );

  // Create a map of subdivision code to name
  const subdivisionNameMap = useMemo(() => {
    const map = new Map<string, string>();
    subdivisions?.forEach((subdivision) => {
      // Clean up names (e.g., "Wales; Cymru" -> "Wales")
      const cleanName = subdivision.name.split(";")[0].split("[")[0].trim();
      map.set(subdivision.code, cleanName);
    });
    return map;
  }, [subdivisions]);

  const isExpired =
    credential.expiration_date && new Date(credential.expiration_date) < now;

  const hasIssuingLocation =
    credential.issuing_country || credential.issuing_region;

  const countryDisplayName = formatCountryName(
    credential.issuing_country,
    i18n.language ?? "en"
  );

  const regionDisplayName = credential.issuing_region
    ? subdivisionNameMap.get(credential.issuing_region) ||
      credential.issuing_region
    : undefined;

  return (
    <OuterContentItem
      key={index}
      title={credential.credential_description}
      subtitle={credential.issuing_institution}
      tag={
        <>
          {credential.in_progress && (
            <PrimaryTag type="subtle">{t("In progress")}</PrimaryTag>
          )}
          {isExpired && <PrimaryTag type="warn">{t("Expired")}</PrimaryTag>}
        </>
      }
      testID={`credential-summary-${index + 1}`}
    >
      <InnerContentItem
        label={t("Qualification name")}
        testID={`credential-summary-description-${index + 1}`}
      >
        {credential.credential_description}
      </InnerContentItem>
      <InnerContentItem
        label={t("Issuing institution or agency")}
        testID={`credential-summary-institution-${index + 1}`}
      >
        {credential.issuing_institution}
      </InnerContentItem>
      {credential.license_id_number && (
        <InnerContentItem
          label={t("Registration or reference number")}
          testID={`credential-summary-license-number-${index + 1}`}
        >
          {credential.license_id_number}
        </InnerContentItem>
      )}
      {hasIssuingLocation && (
        <InnerContentItem
          label={t("Issuing location")}
          testID={`credential-summary-location-${index + 1}`}
        >
          {countryDisplayName}
          {regionDisplayName && (
            <>
              <br />
              {regionDisplayName}
            </>
          )}
        </InnerContentItem>
      )}
      {credential.issue_date && !credential.in_progress && (
        <InnerContentItem
          label={t("Issue date")}
          testID={`credential-summary-issue-date-${index + 1}`}
        >
          {formatDate(credential.issue_date, "MMMM yyyy")}
        </InnerContentItem>
      )}
      {credential.expiration_date && !credential.in_progress && (
        <InnerContentItem
          label={t("Expiration date")}
          testID={`credential-summary-expiration-date-${index + 1}`}
        >
          {formatDate(credential.expiration_date, "MMMM yyyy")}
        </InnerContentItem>
      )}
    </OuterContentItem>
  );
}

export function CredentialHistorySummaryControl(props: ArrayControlProps) {
  const credentials = props.data as Credential[];

  return (
    <>
      {credentials?.map((credential, index) => (
        <CredentialSummaryItem
          key={`${credential.credential_description}-${credential.issuing_institution}-${credential.license_id_number ?? ""}-${index}`}
          credential={credential}
          index={index}
        />
      ))}
    </>
  );
}
